import { UserDto, FullUserDto } from "../interfaces/UserInterfaces";
import { parseApiErrorMessage, parseApiErrorResponse } from "../utils/parseApiError";

interface RegisterRequestOptions {
  User_Password: string;
  User_Email: string;
  User_FirstName: string;
  User_MiddleName?: string;
  User_LastName: string;
  User_DOB: string; // Date of Birth in ISO format
  User_TermsOfService: boolean;
  User_PhoneNumber?: string; // Optional phone number
}

interface FinishAccountCreationOptions {
  User_guid: string;
  User_Password: string;
  User_DOB: string;
  User_TermsOfService: boolean;
  payload: string;
}

class UserApi {
  static async register(options: RegisterRequestOptions): Promise<UserDto> {
    const prom = new Promise<UserDto>(async (resolve, reject) => {
      const response = await fetch((process.env.API_HOST||'') + '/api/v1/user/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          User_Password: options.User_Password,
          User_Email: options.User_Email,
          User_FirstName: options.User_FirstName,
          User_MiddleName: options.User_MiddleName,
          User_LastName: options.User_LastName,
          User_DOB: options.User_DOB,
          User_TermsOfService: options.User_TermsOfService ? true : false,
          User_PhoneNumber: options.User_PhoneNumber || null
        }),
      });
      if (response.ok) {
        const user: UserDto = await response.json();
        resolve(user);
      } else {
        const errorData = await response.json().catch(() => null);
        reject(new Error(parseApiErrorMessage(errorData)));
      }
    });
    return prom;
  }

  static async finishAccountCreation(options: FinishAccountCreationOptions): Promise<FullUserDto> {
    const response = await fetch((process.env.API_HOST||'') + '/api/v1/user/finish-account-creation', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        User_guid: options.User_guid,
        User_Password: options.User_Password,
        User_DOB: options.User_DOB,
        User_TermsOfService: options.User_TermsOfService,
        payload: options.payload
      }),
    });

    if (response.ok) {
      const result = await response.json();
      return result.user;
    } else {
      // This route reports an expired link, a spent link and a guid mismatch as `errors[].msg` with
      // no `message`, so reading `message` alone showed the client the fallback and never the reason.
      const errorData = await response.json().catch(() => null);
      throw new Error(parseApiErrorMessage(errorData, 'Account creation failed'));
    }
  }

  static async verifyAccount(token: string): Promise<{ message: string; verified: boolean }> {
    const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user/verify-account`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ token }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Verification failed' }));
      throw new Error(errorData.message || 'Verification failed');
    }

    return await response.json();
  }

  static async resendVerification(email: string): Promise<{ message: string }> {
    const response = await fetch(`${process.env.API_HOST || ''}/api/v1/user/resend-verification`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ User_Email: email }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to resend verification' }));
      throw new Error(errorData.message || 'Failed to resend verification');
    }

    return await response.json();
  }

  // Manager-only: Get users with search and pagination
  static async getUsersForManager(params: {
    from?: number;
    to?: number;
    User_FirstName?: string;
    User_LastName?: string;
    /** Any part of the client's name — matched against first and last name alike. */
    User_Name?: string;
    User_Email?: string;
    User_CreatedAt_From?: string;
    User_CreatedAt_To?: string;
    User_Active?: boolean;
    User_guid?: string;
    sortBy?: 'User_LastName' | 'User_FirstName' | 'User_CreatedAt' | 'User_IntakeDate';
    sortDir?: 'asc' | 'desc';
  } = {}): Promise<FullUserDto[]> {
    const queryParams = new URLSearchParams();

    if (params.from !== undefined) queryParams.append('from', params.from.toString());
    if (params.to !== undefined) queryParams.append('to', params.to.toString());
    if (params.User_FirstName) queryParams.append('User_FirstName', params.User_FirstName);
    if (params.User_LastName) queryParams.append('User_LastName', params.User_LastName);
    if (params.User_Name) queryParams.append('User_Name', params.User_Name);
    if (params.User_Email) queryParams.append('User_Email', params.User_Email);
    if (params.User_CreatedAt_From) queryParams.append('User_CreatedAt_From', params.User_CreatedAt_From);
    if (params.User_CreatedAt_To) queryParams.append('User_CreatedAt_To', params.User_CreatedAt_To);
    if (params.User_Active !== undefined) queryParams.append('User_Active', params.User_Active.toString());
    if (params.User_guid) queryParams.append('User_guid', params.User_guid);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortDir) queryParams.append('sortDir', params.sortDir);

    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager-user/users?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const users: FullUserDto[] = await response.json();
      return users;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch users' }));
      throw new Error(errorData.message || errorData.error || 'Failed to fetch users');
    }
  }

  /**
   * How many clients came in on each date, newest first. Lloyd, 21 Sep: "a bird's eye view of how
   * many clients were entered day by day." Dates with nobody are absent rather than zero.
   */
  static async getClientIntakeByDay(): Promise<Array<{ date: string; count: number }>> {
    const apiHost = process.env.API_HOST?.trim() || window.location.origin;
    const response = await fetch(`${apiHost}/api/v1/manager-user/users/intake-by-day`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(await parseApiErrorResponse(response, 'Failed to fetch intake by day'));
    }
    return response.json();
  }
}

// Add update user and password reset functionality
interface UpdateUserOptions {
  User_FirstName?: string;
  User_MiddleName?: string;
  User_LastName?: string;
  User_Email?: string;
  User_DOB?: string;
  User_PhoneNumber?: string | null;
}

interface UserDocumentDto {
  id: number;
  name: string;
  guid: string;
  uploadedAt: string;
  updatedAt: string;
}

class UserApiExtended {
  static async updateUser(userGuid: string, options: UpdateUserOptions): Promise<FullUserDto> {
    const response = await fetch((process.env.API_HOST||'') + `/api/v1/user/${userGuid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(options),
    });

    if (response.ok) {
      const user: FullUserDto = await response.json();
      return user;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Update failed' }));
      throw new Error(errorData.message || 'Update failed');
    }
  }

  static async requestPasswordReset(email: string): Promise<string> {
    const response = await fetch((process.env.API_HOST||'') + '/api/v1/user/request-password-reset', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        User_Email: email
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || 'Password reset request failed');
    }
    return data.message || 'Password reset email sent successfully.';
  }

  static async confirmPasswordReset(token: string, newPassword: string): Promise<void> {
    const response = await fetch((process.env.API_HOST||'') + `/api/v1/user/confirm-password-reset/${token}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        newPassword: newPassword
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Password reset failed' }));
      throw new Error(errorData.message || 'Password reset failed');
    }
  }

  static async getUserDocuments(): Promise<UserDocumentDto[]> {
    const response = await fetch((process.env.API_HOST||'') + '/api/v1/documents', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (response.ok) {
      const result = await response.json();
      return result.documents;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch documents' }));
      throw new Error(errorData.error || errorData.message || 'Failed to fetch documents');
    }
  }

  static async uploadUserDocument(file: File): Promise<UserDocumentDto> {
    const formData = new FormData();
    formData.append('document', file);

    const response = await fetch((process.env.API_HOST||'') + '/api/v1/documents/upload', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (response.ok) {
      const result = await response.json();
      return result;
    } else {
      const errorData = await response.json().catch(() => ({ message: 'Failed to upload document' }));
      throw new Error(errorData.error || errorData.message || 'Failed to upload document');
    }
  }

  static async deleteUserDocument(documentGuid: string): Promise<void> {
    const response = await fetch((process.env.API_HOST||'') + `/api/v1/documents/${documentGuid}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to delete document' }));
      throw new Error(errorData.error || errorData.message || 'Failed to delete document');
    }
  }
}

export { UserApiExtended, UserDocumentDto };
export default UserApi;